import { Draggable16, ChevronDown16, TreeView16 } from '@carbon/icons-react';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TaskAPI, StatusGroup, StatusFieldOption } from 'api/tasks';
import {
  CpBody00Secondary,
  CpBody01Primary,
} from 'components/atoms/customer-portal/typography';
import { StatusIndicator } from 'components/molecules/status-indicator';
import {
  MarkasInProgress,
  ChevronWrapper,
  CompletedTag,
} from 'components/organisms/customer-portal/widgets/common';
import { CpTaskCardAssignees } from 'components/organisms/customer-portal/widgets/cp-task-card-assignees';
import { useSelectedStatusOption, useStatusField } from 'data/fields/hooks';
import { Task } from 'data/tasks';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { CpPopover } from '../../modal/cp-popover';
import {
  CpStatusPicker,
  CpStatusPickerProps,
} from '../../status-picker/cp-status-picker';

const SubTaskWrapper = styled.div`
  padding: var(--cp-spacing-05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  border-bottom: 1px solid var(--cp-border-subtle);
  background: var(--cp-white);

  &:hover {
    background: var(--cp-blue-10);
  }
`;

const DragWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  margin-right: var(--cp-spacing-03);
  height: 32px;
  width: 32px;
  justify-content: center;

  svg {
    fill: var(--cp-gray-50);
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TaskName = styled(CpBody01Primary)`
  /* ellipsis */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: var(--cp-spacing-03);
`;

const DetailsWraper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const StatusWrapper = styled.div<{
  isDateAvailable?: boolean;
  isAssigneeAvailable?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: var(--cp-spacing-03);
  margin-left: ${(props) =>
    props.isDateAvailable ? 'var(--cp-spacing-05)' : '0'};
  margin-right: ${(props) =>
    props.isAssigneeAvailable ? 'var(--cp-spacing-05)' : '0'};
`;

const StatusActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--cp-spacing-03);

  @media (max-width: 480px) {
    display: none;
  }
`;

const TreeViewWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: var(--cp-spacing-02);
`;

export const StatusPickerPopover: React.FC<{
  selectedOption: number | undefined;
  onChange: (change: StatusFieldOption) => void;
}> = ({ selectedOption, onChange, children }) => {
  const statusField = useStatusField();

  return (
    <CpPopover
      placement="bottom"
      trigger={['click']}
      getPopupContainer={() =>
        document.getElementById('cp-modal-root') as HTMLElement
      }
      content={
        <CpStatusPicker
          selectedOption={selectedOption}
          title={<FormattedMessage id="task_status" />}
          onChange={onChange}
          options={
            (statusField?.metaData?.options ??
              []) as unknown as CpStatusPickerProps['options']
          }
        />
      }
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    </CpPopover>
  );
};

export const CpSubTaskCard: React.FC<{
  subTask: Task;
  projectId: number;
  refetch?: () => void;
  draggable?: boolean;
  className?: string;
}> = ({ subTask, projectId, refetch, draggable, className }) => {
  const [dirtyState, setDirtyState] = useState(subTask);
  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();
  const selectedStatusOption = useSelectedStatusOption(
    dirtyState?.fields ?? [],
  );

  const dueDate = useMemo(() => dirtyState?.dueDate, [dirtyState?.dueDate]);
  // const startDate = useMemo(() => data?.startDate, [data?.startDate]);
  const date = useMemo(() => {
    // && !startDate
    if (!dueDate) {
      return '';
    }
    // if (!dueDate) {
    //   return moment(startDate).format('DD MMM YY');
    // }
    // if (!startDate) {
    //   return moment(dueDate).format('DD MMM YY');
    // }

    // dont display year if it is current year
    if (moment(dueDate).year() === moment().year()) {
      return moment(dueDate).format('DD MMM');
    }

    return moment(dueDate).format('DD MMM YY');
  }, [dueDate]);

  const updateStatus = useCallback(
    (status: number) => {
      setDirtyState((prev) => {
        const n = {
          ...prev,
          fields: prev.fields?.map((field) => {
            if (field.fieldName?.toLowerCase() === 'status') {
              return {
                ...field,
                fieldValue: status,
              };
            }

            return field;
          }),
        };

        TaskAPI.updateTask(projectId, n.taskId, n).then(() => refetch?.());

        return n;
      });
    },
    [projectId, refetch],
  );

  const onClickMarkAsInProgress: React.MouseEventHandler = useCallback(
    (event) => {
      event.stopPropagation();

      if (selectedStatusOption?.group === StatusGroup.Completed) {
        return;
      }

      const newStatus =
        selectedStatusOption?.group === StatusGroup.InProgress ? 3 : 2;

      updateStatus(newStatus);
    },
    [selectedStatusOption?.group, updateStatus],
  );

  return (
    <SubTaskWrapper
      className={className}
      onClick={() => openTaskDrawer(projectId, subTask.taskId)}
    >
      {draggable ? (
        <DragWrapper>
          <Draggable16 />
        </DragWrapper>
      ) : null}
      <ContentWrapper>
        <TaskName>
          {subTask?.subTasks && subTask?.subTasks.length > 0 ? (
            <TreeViewWrapper>
              <TreeView16 />
            </TreeViewWrapper>
          ) : (
            <></>
          )}
          {subTask?.taskName}
        </TaskName>
        <DetailsWraper>
          <CpBody00Secondary>{date}</CpBody00Secondary>
          <StatusWrapper
            isDateAvailable={!!date}
            isAssigneeAvailable={
              !!dirtyState?.assignee &&
              !!dirtyState?.assignee?.users?.length &&
              !!dirtyState?.assignee?.teams?.length
            }
          >
            {selectedStatusOption ? (
              <StatusIndicator status={selectedStatusOption} />
            ) : (
              <></>
            )}
            <CpBody00Secondary>{selectedStatusOption?.label}</CpBody00Secondary>
            <CpTaskCardAssignees assignee={dirtyState?.assignee} />
          </StatusWrapper>
        </DetailsWraper>
      </ContentWrapper>
      <StatusActionsWrapper>
        {selectedStatusOption?.group === StatusGroup.Completed ? (
          <StatusPickerPopover
            selectedOption={selectedStatusOption?.value}
            onChange={(c) => updateStatus(c?.value)}
          >
            <CompletedTag color={selectedStatusOption?.color}>
              <span className="status-label">
                {selectedStatusOption?.label}
              </span>
              <ChevronDown16 />
            </CompletedTag>
          </StatusPickerPopover>
        ) : (
          <MarkasInProgress onClick={onClickMarkAsInProgress}>
            <span className="status-label">
              <FormattedMessage
                id={
                  selectedStatusOption?.group === StatusGroup.InProgress
                    ? 'customer_portal.widgets.project_plan.task_list.status_picker.mark_as_completed'
                    : 'customer_portal.widgets.project_plan.task_list.status_picker.mark_as_in_progress'
                }
              />
            </span>
          </MarkasInProgress>
        )}
        {selectedStatusOption?.group === StatusGroup.Completed ? (
          <></>
        ) : (
          <StatusPickerPopover
            selectedOption={selectedStatusOption?.value}
            onChange={(c) => updateStatus(c?.value)}
          >
            <ChevronWrapper>
              <ChevronDown16 />
            </ChevronWrapper>
          </StatusPickerPopover>
        )}
      </StatusActionsWrapper>
    </SubTaskWrapper>
  );
};
